import { useCallback, useEffect, useMemo, useState } from 'react';
import { customerSupportService } from '@/services/customerSupportService';
import { KanbanColumn, CreateKanbanColumnDTO, UpdateKanbanColumnDTO } from '@/types/customerSupport';

export function useKanbanColumnConfig() {
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await customerSupportService.listKanbanColumns();
      setColumns(list);
    } catch (err: any) {
      console.error('[useKanbanColumnConfig] Erro ao carregar colunas:', err);
      setError(err?.message || 'Erro ao carregar colunas do kanban');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const persistOrder = useCallback(async (next: KanbanColumn[]) => {
    try {
      const updated = await customerSupportService.reorderKanbanColumns(next.map(c => c.id));
      setColumns(updated);
    } catch (err: any) {
      console.error('[useKanbanColumnConfig] Erro ao reordenar:', err);
      // recarrega para sair do estado inconsistente
      reload();
      throw err;
    }
  }, [reload]);

  const moveUp = useCallback(async (id: number) => {
    setColumns(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      // Disparar persist async — não dá pra await dentro de setState
      persistOrder(next).catch(() => {});
      return next;
    });
  }, [persistOrder]);

  const moveDown = useCallback(async (id: number) => {
    setColumns(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
      persistOrder(next).catch(() => {});
      return next;
    });
  }, [persistOrder]);

  const update = useCallback(async (id: number, data: UpdateKanbanColumnDTO) => {
    const updated = await customerSupportService.updateKanbanColumn(id, data);
    setColumns(prev => prev.map(c => (c.id === id ? updated : c)));
    return updated;
  }, []);

  const toggleVisible = useCallback(async (id: number) => {
    const current = columns.find(c => c.id === id);
    if (!current) return;
    await update(id, { is_active: !current.is_active });
  }, [columns, update]);

  const create = useCallback(async (data: CreateKanbanColumnDTO) => {
    const created = await customerSupportService.createKanbanColumn(data);
    setColumns(prev => [...prev, created]);
    return created;
  }, []);

  const remove = useCallback(async (id: number) => {
    await customerSupportService.deleteKanbanColumn(id);
    setColumns(prev => prev.filter(c => c.id !== id));
  }, []);

  const visibleColumns = useMemo(() => columns.filter(c => c.is_active), [columns]);
  const columnsByKey = useMemo(() => {
    const map: Record<string, KanbanColumn> = {};
    for (const c of columns) map[c.key] = c;
    return map;
  }, [columns]);

  return {
    columns,
    visibleColumns,
    columnsByKey,
    loading,
    error,
    reload,
    moveUp,
    moveDown,
    toggleVisible,
    update,
    create,
    remove,
  };
}
